import React, { useState, cloneElement } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { HeartHandshakeIcon, UsersIcon, HeartIcon, FlameIcon, BookOpenIcon, MapPinIcon, UserIcon, ListIcon, AlertCircleIcon, LayoutGridIcon, LinkIcon, ZapIcon, BookmarkIcon, GlobeIcon } from 'lucide-react';
import { FilterSidebar, FilterSidebarWrapper, NavItem, FilterSection } from '../components/UI/FilterSidebar';
import ContentPageHeader, { getContentPageIcon } from '../components/Content/ContentPageHeader';
import { ContentAccordionSection, sortEntriesAlphabetically } from '../components/UI/ContentAccordion';
import { TagList, TagViewToggle, type TagDisplayMode } from '../components/Content/TagDisplay';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useDarkMode } from '../context/DarkModeContext';
const allTags = [
// Tropes
{
  id: 'enemies-to-lovers',
  name: 'Enemies to Lovers',
  description: 'Characters who initially dislike each other eventually fall in love.',
  count: 245,
  category: 'Tropes'
}, {
  id: 'found-family',
  name: 'Found Family',
  description: 'Characters who are not related by blood form deep familial bonds.',
  count: 187,
  category: 'Tropes'
}, {
  id: 'fake-dating',
  name: 'Fake Dating',
  description: 'Characters pretend to be in a relationship but develop real feelings.',
  count: 210,
  category: 'Tropes'
}, {
  id: 'forced-proximity',
  name: 'Forced Proximity',
  description: 'Characters are forced to spend time together in close quarters.',
  count: 198,
  category: 'Tropes'
}, {
  id: 'slow-burn-trope',
  name: 'Slow Burn',
  description: 'Romantic tension builds slowly throughout the story.',
  count: 176,
  category: 'Tropes'
},
// Characters
{
  id: 'brooding-hero',
  name: 'Brooding Hero',
  description: 'A moody, introspective character who often has a troubled past.',
  count: 156,
  category: 'Characters'
}, {
  id: 'strong-female-lead',
  name: 'Strong Female Lead',
  description: 'A female protagonist who demonstrates independence and courage.',
  count: 213,
  category: 'Characters'
}, {
  id: 'anti-hero',
  name: 'Anti-Hero',
  description: 'Protagonist who lacks conventional heroic qualities.',
  count: 189,
  category: 'Characters'
}, {
  id: 'chosen-one',
  name: 'Chosen One',
  description: 'Character destined for greatness or to fulfill a prophecy.',
  count: 167,
  category: 'Characters'
},
// Relationship Types
{
  id: 'slow-burn',
  name: 'Slow Burn',
  description: 'A romantic relationship that develops gradually over time.',
  count: 178,
  category: 'Relationship Types'
}, {
  id: 'friends-to-lovers',
  name: 'Friends to Lovers',
  description: 'Characters begin as friends before developing romantic feelings.',
  count: 142,
  category: 'Relationship Types'
}, {
  id: 'rivals-to-lovers',
  name: 'Rivals to Lovers',
  description: 'Competing characters develop romantic feelings.',
  count: 134,
  category: 'Relationship Types'
},
// Representation
{
  id: 'lgbtq',
  name: 'LGBTQ+',
  description: 'Books featuring lesbian, gay, bisexual, transgender, and queer characters.',
  count: 328,
  category: 'Representation'
}, {
  id: 'neurodivergent',
  name: 'Neurodivergent',
  description: 'Books with characters who are neurodivergent.',
  count: 156,
  category: 'Representation'
}, {
  id: 'bipoc',
  name: 'BIPOC',
  description: 'Books centering Black, Indigenous, and People of Color.',
  count: 245,
  category: 'Representation'
},
// Themes
{
  id: 'coming-of-age',
  name: 'Coming of Age',
  description: 'Stories focusing on the growth of a protagonist from youth to adulthood.',
  count: 215,
  category: 'Themes'
}, {
  id: 'redemption',
  name: 'Redemption',
  description: 'Stories where characters seek atonement for past actions.',
  count: 178,
  category: 'Themes'
}, {
  id: 'found-identity',
  name: 'Found Identity',
  description: 'Characters discover who they truly are.',
  count: 143,
  category: 'Themes'
},
// Settings
{
  id: 'small-town',
  name: 'Small Town',
  description: 'Stories set in small, close-knit communities.',
  count: 183,
  category: 'Settings'
}, {
  id: 'big-city',
  name: 'Big City',
  description: 'Stories set in large metropolitan areas.',
  count: 167,
  category: 'Settings'
}, {
  id: 'fantasy-world',
  name: 'Fantasy World',
  description: 'Stories set in fully imagined fantasy realms.',
  count: 221,
  category: 'Settings'
},
// Content Warnings
{
  id: 'violence',
  name: 'Violence',
  description: 'Books containing scenes of violence or physical conflict.',
  count: 278,
  category: 'Content Warnings'
}, {
  id: 'sexual-content',
  name: 'Sexual Content',
  description: 'Books containing explicit sexual scenes or references.',
  count: 312,
  category: 'Content Warnings'
}, {
  id: 'grief-loss',
  name: 'Grief & Loss',
  description: 'Books dealing with death, grief, or significant loss.',
  count: 198,
  category: 'Content Warnings'
}];
const categoryIcons: Record<string, JSX.Element> = {
  Tropes: <HeartHandshakeIcon className="h-5 w-5" data-id="element-4845" data-mp="3ryqrta32y820" />,
  Characters: <UserIcon className="h-5 w-5" data-id="element-4846" data-mp="3mymiq54i4flt" />,
  'Relationship Types': <UsersIcon className="h-5 w-5" data-id="element-4847" data-mp="5pg2a4dov296" />,
  Representation: <HeartIcon className="h-5 w-5" data-id="element-4848" data-mp="5q7oiolebnms" />,
  Themes: <BookOpenIcon className="h-5 w-5" data-id="element-4849" data-mp="qcu4b2ewfwz" />,
  Settings: <MapPinIcon className="h-5 w-5" data-id="element-4850" data-mp="pjwq0pcf2ri" />,
  'Content Warnings': <AlertCircleIcon className="h-5 w-5" data-id="element-4851" data-mp="1h4cs3ghp5vhl" />
};
const contentNavItems: NavItem[] = [{
  label: 'View All Tags',
  href: '/all-content-tags',
  icon: LayoutGridIcon,
  isActive: true
}, {
  label: 'Tropes',
  href: '/tropes',
  icon: HeartHandshakeIcon,
  subItems: [{
    label: 'Romance',
    href: '/tropes?category=romance'
  }, {
    label: 'Fantasy',
    href: '/tropes?category=fantasy'
  }, {
    label: 'Mystery',
    href: '/tropes?category=mystery'
  }, {
    label: 'Sci-Fi',
    href: '/tropes?category=scifi'
  }, {
    label: 'Horror',
    href: '/tropes?category=horror'
  }, {
    label: 'General',
    href: '/tropes?category=general'
  }]
}, {
  label: 'Characters',
  href: '/characters',
  icon: UserIcon,
  subItems: [{
    label: 'Romance',
    href: '/characters?category=romance'
  }, {
    label: 'Fantasy',
    href: '/characters?category=fantasy'
  }, {
    label: 'Mystery',
    href: '/characters?category=mystery'
  }, {
    label: 'Sci-Fi',
    href: '/characters?category=scifi'
  }, {
    label: 'Horror',
    href: '/characters?category=horror'
  }, {
    label: 'General',
    href: '/characters?category=general'
  }]
}, {
  label: 'Relationship Types',
  href: '/relationship-types',
  icon: UsersIcon,
  subItems: [{
    label: 'Romance',
    href: '/relationship-types?category=romance'
  }, {
    label: 'Fantasy',
    href: '/relationship-types?category=fantasy'
  }, {
    label: 'Mystery',
    href: '/relationship-types?category=mystery'
  }, {
    label: 'General',
    href: '/relationship-types?category=general'
  }]
}, {
  label: 'Pairing Types',
  href: '/pairing-types',
  icon: LinkIcon,
  subItems: [{
    label: 'Monogamous',
    href: '/pairing-types?category=monogamous'
  }, {
    label: 'Why Choose',
    href: '/pairing-types?category=why-choose'
  }, {
    label: 'Closed Triad',
    href: '/pairing-types?category=closed-triad'
  }, {
    label: 'Polyamorous',
    href: '/pairing-types?category=polyamorous'
  }, {
    label: 'NB / Trans Inclusive',
    href: '/pairing-types?category=nb-trans'
  }, {
    label: 'No Romance',
    href: '/pairing-types?category=no-romance'
  }]
}, {
  label: 'Spice & Darkness',
  href: '/spice-darkness',
  icon: FlameIcon,
  subItems: [{
    label: 'Spice Levels',
    href: '/spice-darkness#spice'
  }, {
    label: 'Darkness Levels',
    href: '/spice-darkness#darkness'
  }]
}, {
  label: 'Burn Levels',
  href: '/burn-levels',
  icon: ZapIcon
}, {
  label: 'Ending Types',
  href: '/ending-types',
  icon: BookmarkIcon
}, {
  label: 'Representation',
  href: '/representation',
  icon: UsersIcon,
  subItems: [{
    label: 'Sexual Orientation & Gender',
    href: '/representation?category=Sexual+Orientation+%26+Gender'
  }, {
    label: 'Race & Ethnicity',
    href: '/representation?category=Race+%26+Ethnicity'
  }, {
    label: 'Neurodiversity & Disability',
    href: '/representation?category=Neurodiversity+%26+Disability'
  }, {
    label: 'Mental Health',
    href: '/representation?category=Mental+Health'
  }]
}, {
  label: 'Cultural Identity',
  href: '/cultural-identity',
  icon: GlobeIcon
}, {
  label: 'Themes',
  href: '/themes',
  icon: BookOpenIcon,
  subItems: [{
    label: 'Personal Growth',
    href: '/themes?category=Personal+Growth'
  }, {
    label: 'Destiny',
    href: '/themes?category=Destiny'
  }, {
    label: 'Power',
    href: '/themes?category=Power'
  }, {
    label: 'Morality',
    href: '/themes?category=Morality'
  }, {
    label: 'Relationships',
    href: '/themes?category=Relationships'
  }, {
    label: 'Identity',
    href: '/themes?category=Identity'
  }]
}, {
  label: 'Settings',
  href: '/settings',
  icon: MapPinIcon,
  subItems: [{
    label: 'Contemporary',
    href: '/settings?category=Contemporary'
  }, {
    label: 'Historical',
    href: '/settings?category=Historical'
  }, {
    label: 'Fantasy',
    href: '/settings?category=Fantasy'
  }, {
    label: 'Science Fiction',
    href: '/settings?category=Science%20Fiction'
  }]
}, {
  label: 'Content Warnings',
  href: '/content-warnings',
  icon: AlertCircleIcon
}];
const AllContentTags = () => {
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [sortOption, setSortOption] = useState<'alphabetical' | 'bookCount' | 'viewAll'>('viewAll');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [tagViewMode, setTagViewMode] = useState<TagDisplayMode>('cards');
  const dk = useDarkClasses();
  // Helper function to get color based on tag category
  const getTagColor = (category: string) => {
    if (dk.isDark) {
      switch (category) {
        case 'Tropes':
          return 'bg-indigo-900/30 text-indigo-300';
        case 'Characters':
          return 'bg-orange-900/30 text-orange-300';
        case 'Relationship Types':
          return 'bg-pink-900/30 text-pink-300';
        case 'Representation':
          return 'bg-purple-900/30 text-purple-300';
        case 'Themes':
          return 'bg-blue-900/30 text-blue-300';
        case 'Settings':
          return 'bg-green-900/30 text-green-300';
        case 'Content Warnings':
          return 'bg-amber-900/30 text-amber-300';
        default:
          return dk.badgeNeutral;
      }
    }
    switch (category) {
      case 'Tropes':
        return 'bg-indigo-100 text-indigo-900';
      case 'Characters':
        return 'bg-orange-100 text-orange-900';
      case 'Relationship Types':
        return 'bg-pink-100 text-pink-900';
      case 'Representation':
        return 'bg-purple-100 text-purple-900';
      case 'Themes':
        return 'bg-blue-100 text-blue-900';
      case 'Settings':
        return 'bg-green-100 text-green-900';
      case 'Content Warnings':
        return 'bg-amber-100 text-amber-900';
      default:
        return 'bg-gray-100 text-gray-900';
    }
  };
  const filteredTags = allTags.filter(tag => {
    const matchesSearch = !searchTerm || tag.name.toLowerCase().includes(searchTerm.toLowerCase()) || tag.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || tag.category.toLowerCase() === selectedCategory.toLowerCase();
    return matchesSearch && matchesCategory;
  });
  const sortedTags = [...filteredTags].sort((a, b) => {
    if (sortOption === 'bookCount') return b.count - a.count;
    return a.name.localeCompare(b.name);
  });
  // Group by category for accordion view
  const groupedTags = sortedTags.reduce((acc, tag) => {
    if (!acc[tag.category]) acc[tag.category] = [];
    acc[tag.category].push(tag);
    return acc;
  }, {} as Record<string, typeof allTags>);
  const labelMap: Record<string, string> = {};
  Object.keys(categoryIcons).forEach(k => {
    labelMap[k] = k;
  });
  const sortedEntries = sortEntriesAlphabetically(Object.entries(groupedTags), labelMap);
  const filterSections: FilterSection[] = [{
    id: 'category',
    title: 'Category',
    type: 'radio',
    options: [{
      value: 'all',
      label: 'All Categories'
    }, {
      value: 'Tropes',
      label: 'Tropes'
    }, {
      value: 'Characters',
      label: 'Characters'
    }, {
      value: 'Relationship Types',
      label: 'Relationship Types'
    }, {
      value: 'Representation',
      label: 'Representation'
    }, {
      value: 'Themes',
      label: 'Themes'
    }, {
      value: 'Settings',
      label: 'Settings'
    }, {
      value: 'Content Warnings',
      label: 'Content Warnings'
    }],
    value: selectedCategory,
    onChange: (val: string) => setSelectedCategory(val)
  }, {
    id: 'sort',
    title: 'Sort By',
    type: 'radio',
    options: [{
      value: 'alphabetical',
      label: 'Alphabetical'
    }, {
      value: 'bookCount',
      label: 'Book Count'
    }, {
      value: 'viewAll',
      label: 'View All (flat list)'
    }],
    value: sortOption,
    onChange: (val: string) => setSortOption(val as typeof sortOption)
  }];
  const handleReset = () => {
    setSelectedCategory('all');
    setSortOption('alphabetical');
    setSearchTerm('');
  };
  const toDisplayTags = (items: typeof allTags) => items.map(tag => ({
    id: tag.id,
    name: tag.name,
    description: tag.description,
    count: tag.count,
    category: tag.category,
    href: `/tagged-books/${tag.id}`
  }));
  const renderTags = () => {
    if (sortOption === 'viewAll' || selectedCategory !== 'all' || searchTerm) {
      return <TagList tags={toDisplayTags(sortedTags)} mode={tagViewMode} colorClassFor={t => getTagColor(t.category || '')} showCategoryOnCard={selectedCategory === 'all' && !searchTerm} data-id="element-4852" data-mp="kdl80448a5vw" />;
    }
    return <div className="space-y-3" data-id="element-4853" data-mp="20nxdmdpt153d">
        {sortedEntries.map(([category, tags]) => <ContentAccordionSection key={category} title={category} icon={cloneElement(categoryIcons[category] || <ListIcon className="h-5 w-5" data-id="element-4855" data-mp="alexcpjlvdjg" />, {
        className: 'h-5 w-5'
      })} itemCount={tags.length} data-id="element-4854" data-mp="m7ifxx83au8">
            <TagList tags={toDisplayTags(tags)} mode={tagViewMode} colorClassFor={t => getTagColor(t.category || '')} data-id="element-4856" data-mp="5k93upi4fm8v" />
          </ContentAccordionSection>)}
      </div>;
  };
  return <div className={dk.page} data-id="element-4857" data-mp="3ns8ae8j5952h">
      <ContentPageHeader title="All Content Tags" icon={getContentPageIcon('viewall')} searchPlaceholder="Search all tags..." searchQuery={searchTerm} setSearchQuery={setSearchTerm} showFilters={sidebarOpen} setShowFilters={setSidebarOpen} data-id="element-4858" data-mp="3ntjds78ogat2" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-4859" data-mp="2qcaxx7x774ax">
        <div className="flex gap-8" data-id="element-4860" data-mp="15th7csjoi89g">
          <FilterSidebarWrapper isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} data-id="element-4861" data-mp="15sfk7ppe1vbd">
            <FilterSidebar title="Browse" navigation={contentNavItems} sections={filterSections} onReset={handleReset} searchable={false} data-id="element-4862" data-mp="10uxh4fy1yaba" />
          </FilterSidebarWrapper>
          <main className="flex-1 min-w-0" data-id="element-4863" data-mp="1ti0ev5uwmh0h">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4" data-id="element-4864" data-mp="1fl4tcdxgux3j">
              <p className={`text-sm ${dk.textSecondary}`} data-id="element-4865" data-mp="1kj6cszyt0yfj">
                {sortedTags.length} {sortedTags.length === 1 ? 'tag' : 'tags'}
              </p>
              <TagViewToggle mode={tagViewMode} setMode={setTagViewMode} data-id="element-4866" data-mp="43vc7y1t8h5e" />
            </div>
            {sortedTags.length === 0 ? <div className="text-center py-12" data-id="element-4867" data-mp="zu347l101gyz">
                <p className={dk.textMuted} data-id="element-4868" data-mp="zvwct5v6ab9m">
                  No tags found matching your search.
                </p>
              </div> : renderTags()}
          </main>
        </div>
      </div>
    </div>;
};
export default AllContentTags;